import { useCallback, useEffect, useState } from "react";
import { Lightbulb, Landmark, UtensilsCrossed, CalendarDays, FileText, Link2, AlertTriangle, Plus, Pencil, Trash2, ExternalLink, Loader2, User } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* "Know this destination? Help travelers." — user-submitted tips, attractions,
   dining, events, articles, links and warnings for a destination guide.
   Community content, clearly separated from the editorial guide. */

const TYPE_META = {
  tip: ["Travel tip", Lightbulb, "#34A853"], attraction: ["Attraction", Landmark, "#4285F4"],
  dining: ["Dining", UtensilsCrossed, "#EA4335"], event: ["Event", CalendarDays, "#7C3AED"],
  article: ["Article", FileText, "#0D9488"], link: ["Useful link", Link2, "#4285F4"],
  warning: ["Warning", AlertTriangle, "#EA4335"],
};
const inp = "w-full border border-zinc-300 bg-white px-3 py-2 text-sm";
const EMPTY = { type: "tip", title: "", description: "", link: "", tags: "", notes: "" };

function ContribForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, tags: Array.isArray(initial?.tags) ? initial.tags.join(", ") : (initial?.tags || "") });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="border border-zinc-300 bg-zinc-50 p-4 space-y-2" data-testid="contrib-form">
      <div className="grid sm:grid-cols-2 gap-2">
        <select value={f.type} onChange={set("type")} className={inp} data-testid="contrib-type">
          {Object.entries(TYPE_META).map(([k, [label]]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <input value={f.title} onChange={set("title")} placeholder="Title" className={inp} data-testid="contrib-title" />
      </div>
      <textarea value={f.description} onChange={set("description")} placeholder="Describe it for travelers" rows={3} className={inp} data-testid="contrib-description" />
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={f.link} onChange={set("link")} placeholder="Link (optional, https://…)" className={inp} data-testid="contrib-link" />
        <input value={f.tags} onChange={set("tags")} placeholder="Tags, comma-separated (food, family, budget)" className={inp} data-testid="contrib-tags" />
      </div>
      <input value={f.notes} onChange={set("notes")} placeholder="Extra notes (opening season, how to get there…)" className={inp} data-testid="contrib-notes" />
      <div className="flex gap-2">
        <button onClick={() => onSave({ ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) })}
          disabled={f.title.trim().length < 2} data-testid="contrib-save"
          className="px-4 py-2 bg-[#34A853] text-white text-xs font-700 disabled:opacity-40">Submit</button>
        <button onClick={onCancel} className="px-4 py-2 text-xs text-muted-foreground">Cancel</button>
      </div>
      <p className="text-[10px] text-muted-foreground">Want to add photos? Use the photo gallery below — uploads are moderated and location data is scrubbed automatically.</p>
    </div>
  );
}

export default function GeoContributions({ slug, placeName }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get(`/geo-contrib/${slug}`).then((r) => setData(r.data)).catch(() => setData({ contributions: [] }));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (data === null) return <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />;

  const save = async (f, id) => {
    try {
      if (id) await api.put(`/geo-contrib/id/${id}`, f);
      else await api.post(`/geo-contrib/${slug}`, f);
      toast.success(id ? "Updated" : "Thanks for helping travelers!");
      setAdding(false); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this contribution?")) return;
    await api.delete(`/geo-contrib/id/${id}`); load();
  };

  const rows = data.contributions.filter((c) => filter === "all" || c.type === filter);

  return (
    <div data-testid="geo-contributions">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setFilter("all")}
          className={`px-2.5 py-1 text-xs font-700 border ${filter === "all" ? "bg-zinc-800 text-white border-zinc-800" : "bg-white border-zinc-300"}`}>
          All ({data.contributions.length})
        </button>
        {Object.entries(TYPE_META).map(([k, [label, Icon, color]]) => {
          const n = data.contributions.filter((c) => c.type === k).length;
          if (!n && filter !== k) return null;
          return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`contrib-filter-${k}`}
              style={filter === k ? { backgroundColor: color, borderColor: color, color: "#fff" } : undefined}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-700 border bg-white border-zinc-300">
              <Icon className="w-3 h-3" /> {label} ({n})
            </button>
          );
        })}
        {user ? (
          !adding && (
            <button onClick={() => { setAdding(true); setEditing(null); }} data-testid="contrib-add"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#4285F4] text-white text-xs font-700 hover:opacity-90">
              <Plus className="w-3.5 h-3.5" /> Share what you know
            </button>
          )
        ) : (
          <span className="ml-auto text-xs text-muted-foreground">Sign in to share tips, places and warnings.</span>
        )}
      </div>

      {adding && <div className="mb-4"><ContribForm initial={{}} onSave={(f) => save(f)} onCancel={() => setAdding(false)} /></div>}

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="contrib-empty">
          No traveler contributions for {placeName} yet — know this destination? Be the first to help.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map((c) => {
            const [label, Icon, color] = TYPE_META[c.type] || TYPE_META.tip;
            return (
              <div key={c.id} className="bg-white border border-zinc-300 p-4" data-testid={`contrib-card-${c.id}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Icon className="w-4 h-4 shrink-0" style={{ color }} />
                  <span className="text-[10px] font-700 uppercase tracking-wide" style={{ color }}>{label}</span>
                  {c.type === "warning" && <span className="text-[10px] bg-red-50 text-red-700 px-1.5 py-0.5 border border-red-200">traveler-reported</span>}
                  {c.is_owner && (
                    <span className="ml-auto flex gap-1">
                      <button onClick={() => { setEditing(c); setAdding(false); }} className="p-1 text-zinc-400 hover:text-primary" data-testid={`contrib-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(c.id)} className="p-1 text-zinc-400 hover:text-rose-600" data-testid={`contrib-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </span>
                  )}
                </div>
                <div className="font-700 text-sm">{c.title}</div>
                {c.description && <p className="text-xs text-zinc-700 mt-1">{c.description}</p>}
                {c.notes && <p className="text-xs text-zinc-500 mt-1 italic">{c.notes}</p>}
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  {c.link && (
                    <a href={c.link} target="_blank" rel="noopener noreferrer nofollow" data-testid={`contrib-link-${c.id}`}
                      className="text-[11px] font-700 text-[#4285F4] hover:underline inline-flex items-center gap-0.5">
                      Visit link <ExternalLink className="w-3 h-3" /></a>
                  )}
                  {(c.tags || []).map((t) => <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 bg-zinc-100 text-zinc-600">{t}</span>)}
                  <span className="ml-auto text-[10px] text-muted-foreground inline-flex items-center gap-1">
                    <User className="w-3 h-3" /> {c.user_name} · {c.created_at?.slice(0, 10)}
                  </span>
                </div>
                {editing?.id === c.id && (
                  <div className="mt-3"><ContribForm initial={editing} onSave={(f) => save(f, c.id)} onCancel={() => setEditing(null)} /></div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
